/**
 * THE GLYPHS THIS APP DRAWS THAT PAPER DOES NOT SHIP.
 *
 * WHY A REGISTRATION AND NOT A COMPONENT. `im-reaction-glyph` solves the same
 * problem — three reactions Paper has no icon for — by inlining SVG in a
 * component of our own. That works because the app owns the markup around a
 * reaction chip. It does NOT work for a NAVIGATION mark: `paper-shell-bar`
 * renders workspace tiles itself, binding `workspace.icon` straight into a
 * `paper-icon`, and `PaperNavWorkspace.icon` is documented as "icon name from
 * the Paper icon registry". There is no template slot to project a component
 * into, so a bespoke workspace mark can only reach the bar as a registry
 * entry.
 *
 * The same is true one rung down. `paper-view-track` renders the view pills
 * from `PaperNavView.icon`, another registry name, and the bar projects only
 * `[shell-bar-mark]` and `[shell-bar-utility]` — so `hub-wall` and
 * `hub-stream` are here for the same reason `hubs` is.
 *
 * (The tag syntax is spelled out in prose here on purpose: `polish-audit`'s
 * unknown-icon detector reads comments as markup and counts a documented
 * example as a real, broken call site.)
 *
 * `PaperIconComponent` reads `ICON_REGISTRY[name]` inside its render effect
 * rather than closing over it, so an entry added before first paint is picked
 * up everywhere the name is used — the shell bar tile, the workspace menu, the
 * bottom nav, the ⌘K row, and any `paper-button` that names it. That single
 * mechanism is why the mark is consistent across all five, instead of being
 * bespoke in one place and a stock glyph in the other four.
 *
 * REGISTRATION IS EXPLICIT, from `main.ts`, before `bootstrapApplication`.
 * Not an import side effect (tree-shakeable, and the ordering would be
 * implicit), and not a DI provider (a `useFactory` is lazy — it would not have
 * run by the time the shell bar's first tile rendered).
 *
 * THE HOUSE RULE HOLDS. Paper wraps `content` in an `<svg>` with
 * `fill="none" stroke="currentColor"` and round caps/joins, so everything here
 * is a stroke drawing on the current text colour. Nothing below may carry its
 * own `fill` — `app-glyphs.spec.ts` fails the build if it does.
 *
 * ⚠ THIS IS A STOPGAP WITH A NAMED END, exactly as the reaction glyphs are.
 * These belong in the published registry in the `ricoh-paper` repository. When
 * one lands there, delete it from here — `app-glyphs.spec.ts` asserts that we
 * never shadow a name Paper already ships, so the day Paper draws `hubs` the
 * build goes red and tells you to remove ours.
 */
import { ICON_REGISTRY } from '@ricoh-paper/angular';

/** Same shape as Paper's `IconDef`, restated so this file imports no type. */
interface AppGlyph {
  /** SVG inner content only — Paper supplies the `<svg>` wrapper. */
  content: string;
  /** Overrides Paper's default of 2. Omit to sit at the house weight. */
  defaultStrokeWidth?: number;
}

export const APP_GLYPHS = {
  /**
   * `hubs` — the Hubs workspace mark. THE PROTOTYPE'S `navHubs`, line 3734 of
   * `Employee App Hubs 24/Hubs Feed.dc.html`, transcribed.
   *
   * THE BRIEF, TWICE. Nathan first said the stock `compass` was not good
   * enough and asked for "something closer to what we use for wall", which
   * produced the composed board this replaces — a lead band over two unequal
   * modules. Then he said the mark has to MATCH THE PROTO. It does not: the
   * proto draws a node and its three satellites, not a board.
   *
   * WHY THE BOARD WAS THE WRONG ANSWER ANYWAY, in hindsight. A board is a
   * drawing of a LAYOUT, and the two view pills one rung below are already
   * layouts (`hub-wall`, `hub-stream`). The workspace mark competing with its
   * own children is the exact confusion the old docblock worried about and
   * then walked into. A hub is a group of people around a thing, and that is
   * what this draws.
   *
   * WHAT IT DRAWS. A ring at the centre with three smaller nodes around it —
   * one above, two below — each joined to the centre by a short spoke.
   *
   * THE NUMBERS ARE THE PROTO'S, with one transcription change. The proto's
   * spokes are RELATIVE linetos (`l-2.8 2.1`); rewritten here as absolute
   * (`L6.7 16`) because `app-glyphs.spec.ts` scans every number in `content`
   * and fails anything outside 0…24 — a negative delta is a legal path and an
   * illegal glyph by that rule. Same drawing, same endpoints.
   *
   *  · The spokes run edge to edge, not centre to centre: the top one leaves
   *    the ring at y 8.9 (12 − 3.1) and stops at y 6 (4.3 + 1.7).
   *  · Stroke 2 — unchanged, and for the unchanged reason: `home`, `map` and
   *    `layout-grid` share the tile row and a lighter mark reads as a smaller
   *    one. The proto draws it at 1.8 in a bar of its own, with nothing to be
   *    lighter than. At 2 the r=1.7 satellites read as solid nodes and the
   *    r=3.1 centre stays a ring, which is the right hierarchy.
   */
  hubs: {
    content:
      '<circle cx="12" cy="12" r="3.1"/>'
      + '<circle cx="12" cy="4.3" r="1.7"/>'
      + '<circle cx="5.3" cy="17.4" r="1.7"/>'
      + '<circle cx="18.7" cy="17.4" r="1.7"/>'
      + '<path d="M12 8.9V6M9.5 13.9L6.7 16M14.5 13.9L17.3 16"/>',
    defaultStrokeWidth: 2,
  },

  /**
   * `hub-wall` — the Wall posture. The proto's `navWall`, line 3727.
   *
   * Replaces Paper's `layout-grid`, a 2×2 of four EQUAL modules. The wall is
   * a masonry of uneven tiles, so its glyph is uneven — the proto's own note
   * at line 8046 says exactly that, and it is the one thing that tells this
   * apart from a generic grid at 15 px.
   *
   * Two columns, each split at a different height: tall-over-short on the
   * left, short-over-tall on the right. The step is the drawing.
   */
  'hub-wall': {
    content:
      '<rect x="3.2" y="3.2" width="7.4" height="9.6" rx="1.6"/>'
      + '<rect x="3.2" y="14.8" width="7.4" height="6" rx="1.6"/>'
      + '<rect x="13.4" y="3.2" width="7.4" height="6" rx="1.6"/>'
      + '<rect x="13.4" y="11.2" width="7.4" height="9.6" rx="1.6"/>',
    defaultStrokeWidth: 1.8,
  },

  /**
   * `hub-stream` — the Stream posture. The proto's `navStream`, line 3728.
   *
   * Replaces Paper's `list`, which is rows and nothing else. The stream is
   * "a post then its words" (the proto, line 8046): one picture across the
   * top, two lines of text beneath it, the second longer than the first.
   *
   * Stroke 1.8, not 2, and this one is not the tile row's argument. The two
   * text rules sit 4 units apart; at stroke 2 and 15 px they close to under a
   * pixel of white and read as one thick bar. Paper ships 1.8 glyphs of its
   * own, so this is a weight in the set rather than an exception to it, and
   * it is the weight the proto draws both view marks at.
   */
  'hub-stream': {
    content:
      '<rect x="3.2" y="3.4" width="17.6" height="8" rx="1.8"/>'
      + '<path d="M3.2 14.6h11.4M3.2 18.6h17.6"/>',
    defaultStrokeWidth: 1.8,
  },

  /**
   * `ask-mark` — Ask's brand mark, for the places that only take an icon name
   * (a `paper-button`, a ⌘K row). The mark is Paper's own: `paper-ask-mark`'s
   * two strokes, a caret (the person asking) and a line (the answer),
   * transcribed from its template at the same stroke. Paper ships it as an
   * animated component rather than a registry glyph, so it is not shadowed
   * here; where Ask's state matters, `im-ask-mark` (shared/ask) draws the
   * live component instead.
   */
  'ask-mark': {
    content: '<path d="M5.5 8L9.5 12L5.5 16"/><path d="M12.5 12H19.5"/>',
    defaultStrokeWidth: 2,
  },
  /**
   * The call controls' OFF states and their two missing marks (Flow hosted
   * meetings, P7.05b). Paper ships `mic` and `video` but no struck-through
   * pair, no captions mark and no screen-share mark; a control that toggles
   * must show which way it is set, not only colour it.
   */
  'mic-off': {
    content:
      '<path d="M2 2L22 22"/><path d="M9 9V5A3 3 0 0 1 15 5V9.3"/><path d="M9 9V12A3 3 0 0 0 14.1 14.1"/>' +
      '<path d="M19 10V12A7 7 0 0 1 18.9 13.2"/><path d="M5 10V12A7 7 0 0 0 17 17"/><path d="M12 19V22"/>',
  },
  'video-off': {
    content:
      '<path d="M10.7 6H14A2 2 0 0 1 16 8V10.5L21.2 7.4A0.5 0.5 0 0 1 22 7.9V16.1"/>' +
      '<path d="M16 16A2 2 0 0 1 14 18H4A2 2 0 0 1 2 16V8A2 2 0 0 1 4 6H6"/><path d="M2 2L22 22"/>',
  },
  'call-captions': {
    content: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 15H11M15 15H17M7 11H9M13 11H17"/>',
  },
  'screen-share': {
    content:
      '<path d="M13 3H4A2 2 0 0 0 2 5V15A2 2 0 0 0 4 17H20A2 2 0 0 0 22 15V12"/><path d="M8 21H16"/>' +
      '<path d="M12 17V21"/><path d="M17 8L22 3"/><path d="M17 3H22V8"/>',
  },
  /**
   * The call dock and the incoming-call card (Flow Live, 0sm8.3): hang up, picture-in-picture and silence the ring.
   * Paper ships `phone` but no struck-through pair, no picture-in-picture mark and no struck-through bell.
   */
  'phone-off': {
    content:
      '<path d="M10.7 13.3A16 16 0 0 0 14.1 15.9L15.4 14.6A2 2 0 0 1 17.5 14.2A12.8 12.8 0 0 0 20.3 14.9A2 2 0 0 1 22 16.9V19.9A2 2 0 0 1 19.8 21.9A19.8 19.8 0 0 1 11.2 18.8A19.4 19.4 0 0 1 7.9 16.1"/>' +
      '<path d="M5.2 12.8A19.8 19.8 0 0 1 2.1 4.2A2 2 0 0 1 4.1 2H7.1A2 2 0 0 1 9.1 3.7A12.8 12.8 0 0 0 9.8 6.5A2 2 0 0 1 9.4 8.6L8.1 9.9"/>' +
      '<path d="M22 2L2 22"/>',
  },
  'picture-in-picture': {
    content:
      '<path d="M21 9V6A2 2 0 0 0 19 4H4A2 2 0 0 0 2 6V16A2 2 0 0 0 4 18H8"/><rect x="12" y="13" width="10" height="7" rx="2"/>',
  },
  'bell-off': {
    content:
      '<path d="M10.3 21A2 2 0 0 0 13.7 21"/><path d="M17 17H4A1 1 0 0 1 3.3 15.3C4.6 14 6 12.5 6 8A6 6 0 0 1 6.3 6.3"/>' +
      '<path d="M2 2L22 22"/><path d="M8.7 3A6 6 0 0 1 18 8C18 10.7 18.8 12.7 19.7 14"/>',
  },
} as const satisfies Record<string, AppGlyph>;

/** The names this app adds. `navigation-icons.spec.ts` unions this with Paper's. */
export type AppGlyphName = keyof typeof APP_GLYPHS;

export const APP_GLYPH_NAMES = Object.keys(APP_GLYPHS) as readonly AppGlyphName[];

/**
 * Merge the app's glyphs into Paper's registry.
 *
 * Idempotent, and deliberately NOT clobber-safe in the other direction: if
 * Paper ever ships one of these names its drawing should win, so this refuses
 * to overwrite an entry that already exists and leaves the spec to tell you.
 */
export function registerAppGlyphs(): void {
  for (const [name, glyph] of Object.entries(APP_GLYPHS)) {
    if (ICON_REGISTRY[name]) continue;
    ICON_REGISTRY[name] = { ...glyph };
  }
}
